import { z } from "zod";
import { ProviderSelection } from "./providers.ts";
import { Team } from "./teams.ts";

export const AutomationScope = z.enum(["personal", "team", "org"]);
export type AutomationScope = z.infer<typeof AutomationScope>;

/** Every client has to resolve the stored zone again — Swift through
 * `TimeZone(identifier:)`, which only understands IANA names. `Intl` also
 * accepts a bare UTC offset ("+05:30") and echoes it back, so a zone that
 * resolves is not necessarily one a client can read: store the canonical
 * identifier and reject offsets. */
export function canonicalTimeZone(zone: string): string | undefined {
  let resolved: string;
  try { resolved = new Intl.DateTimeFormat("en", { timeZone: zone }).resolvedOptions().timeZone; }
  catch { return undefined; }
  return /^[+-]/.test(resolved) ? undefined : resolved;
}

export const AUTOMATION_WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5];

export const AutomationSchedule = z.object({
  kind: z.enum(["hourly", "daily", "weekly"]),
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  /** Sunday = 0 through Saturday = 6. Only a weekly schedule reads it; stored
   * ascending and without repeats, so an unchanged set compares equal. */
  daysOfWeek: z.array(z.number().int().min(0).max(6)).min(1).max(7)
    .transform((days) => [...new Set(days)].sort((a, b) => a - b)),
  timezone: z.string().min(1).max(100).transform((zone, ctx) => {
    const canonical = canonicalTimeZone(zone);
    if (!canonical) {
      ctx.addIssue({ code: "custom", message: "Choose a valid time zone." });
      return z.NEVER;
    }
    return canonical;
  }),
});
export type AutomationSchedule = z.infer<typeof AutomationSchedule>;

const AutomationFields = z.object({
  name: z.string().trim().min(1).max(100),
  prompt: z.string().trim().min(1).max(20_000),
  repoIds: z.array(z.string().min(1).max(100)).min(1).max(20)
    .refine((ids) => new Set(ids).size === ids.length, "Choose each repository once."),
  scope: AutomationScope,
  teamId: z.string().min(1).max(100).nullable(),
  /** A worker cluster to route every run through, or null for "Auto" — the
   * same routing choice task creation offers, rather than a second one. */
  clusterId: z.string().min(1).max(100).nullable(),
  provider: ProviderSelection,
  schedule: AutomationSchedule,
  enabled: z.boolean(),
});

export const SaveAutomationRequest = AutomationFields.refine(
  (value) => (value.scope === "team") === (value.teamId !== null),
  { message: "Choose a team only for a team automation.", path: ["teamId"] },
);
export type SaveAutomationRequest = z.infer<typeof SaveAutomationRequest>;
export const SetAutomationEnabledRequest = z.object({ enabled: z.boolean() });

export const Automation = AutomationFields.extend({
  id: z.string(), orgId: z.string(), createdBy: z.string(),
  createdAt: z.number(), updatedAt: z.number(),
  nextRunAt: z.number().nullable(), lastRunAt: z.number().nullable(),
  /** Why the scheduler paused this automation on its own — a broken schedule,
   * or a creator who lost access. Null whenever a person last set `enabled`. */
  pausedReason: z.string().nullable(),
  canManage: z.boolean(),
});
export type Automation = z.infer<typeof Automation>;
export const AutomationRun = z.object({
  id: z.string(), automationId: z.string(), repoId: z.string(),
  trigger: z.enum(["scheduled", "manual"]),
  scheduledFor: z.number(), createdAt: z.number(), completedAt: z.number().nullable(),
  status: z.enum(["queued", "running", "succeeded", "failed", "skipped"]),
  worktreeId: z.string().nullable(), chatId: z.string().nullable(), error: z.string().nullable(),
});
export type AutomationRun = z.infer<typeof AutomationRun>;

/** Written verbatim into `AutomationRun.error` when an occurrence is skipped
 * because the previous one had not finished. Clients compare against it to
 * tell that skip apart from a missed or paused occurrence. */
export const AUTOMATION_SKIPPED_OVERLAP = "Skipped — the previous run was still going.";
/** An occurrence that arrived too late to be worth starting. */
export const AUTOMATION_SKIPPED_MISSED = "Missed the scheduled run beyond its grace period.";

export const AutomationsResponse = z.object({ automations: z.array(Automation) });
export type AutomationsResponse = z.infer<typeof AutomationsResponse>;
export const AutomationResponse = z.object({ automation: Automation });
export type AutomationResponse = z.infer<typeof AutomationResponse>;
export const AutomationRunsResponse = z.object({ runs: z.array(AutomationRun) });
export type AutomationRunsResponse = z.infer<typeof AutomationRunsResponse>;
export const AutomationOptionsResponse = z.object({ teams: z.array(Team) });
export type AutomationOptionsResponse = z.infer<typeof AutomationOptionsResponse>;

/** Calendar schedules use the saved time zone, including daylight saving.
 * Missing wall-clock times are skipped; repeated times run once, at the first
 * occurrence. The result is strictly after `after` (Unix milliseconds). */
export function nextAutomationRunAt(schedule: AutomationSchedule, after: number): number {
  if (!Number.isFinite(after)) throw new Error("Invalid schedule date");
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: schedule.timezone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  });
  const wallTime = (timestamp: number) => {
    const parts = Object.fromEntries(formatter.formatToParts(timestamp).map((part) => [part.type, part.value]));
    return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour), Number(parts.minute), Number(parts.second));
  };
  const current = new Date(wallTime(after));
  const start = Date.UTC(current.getUTCFullYear(), current.getUTCMonth(), current.getUTCDate());
  const [hour, minute] = schedule.time.split(":").map(Number) as [number, number];
  const hours = schedule.kind === "hourly" ? Array.from({ length: 24 }, (_, i) => i) : [hour];
  // A weekly occurrence can land in the spring-forward gap. Search through
  // the following week as well, instead of rejecting a valid schedule.
  for (let day = 0; day < 16; day++) {
    const midnight = start + day * 86_400_000;
    const weekday = new Date(midnight).getUTCDay();
    if (schedule.kind === "weekly" && !schedule.daysOfWeek.includes(weekday)) continue;
    // Probe both sides of offset transitions, including half-hour DST shifts.
    const offsets = new Set([-36, -12, 0, 12, 36].map((h) => {
      const probe = midnight + h * 3_600_000;
      return wallTime(probe) - probe;
    }));
    for (const targetHour of hours) {
      const target = midnight + targetHour * 3_600_000 + minute * 60_000;
      const candidates = [...offsets].map((offset) => target - offset)
        .filter((candidate) => wallTime(candidate) === target).sort((a, b) => a - b);
      const first = candidates[0];
      if (first !== undefined && first > after) return first;
    }
  }
  throw new Error("Could not find the next scheduled run");
}
